/**
 * Database Info — how big everything is, and when we last looked.
 *
 * A TAB ON STATUS, not a page of its own (Ben, 2026-08-30). It answers the
 * same question the rest of Status does — what shape is this environment in —
 * and a rail entry per question is how the console got to one screen per
 * table. `/database-info` redirects here; the component stays a component.
 *
 * READS A CACHE, ALWAYS, and says so at the top. Counting rows across ~88M
 * of tracking data on every render would be a self-inflicted load test; a
 * nightly job writes the snapshot and this shows its age. A size without its
 * age is a number people trust for longer than they should.
 *
 * Refresh is asynchronous and writes a NEW snapshot, so a refresh that dies
 * halfway leaves the previous one intact.
 */
import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import './database-info.css'

type Table = {
  schema: string
  table: string
  rows: number | null
  rows_exact: boolean
  total_bytes: number | null
  table_bytes: number | null
  index_bytes: number | null
  previous_bytes: number | null
  dead_rows: number | null
  // Non-null only when the server judged the table bloated: N x its own
  // leanest recorded bytes-per-row. The threshold lives server-side.
  bloat_ratio: number | null
}
type Payload = {
  collected_at: string | null
  previous_at: string | null
  refresh: { running: boolean; started_at: string | null; finished_at: string | null; error: string | null }
  totals: { tables: number; bytes: number; rows: number }
  tables: Table[]
}
type State =
  | { status: 'loading' }
  | { status: 'ready'; data: Payload }
  | { status: 'error'; message: string }

const bytes = (n: number | null) => {
  if (n === null || n === undefined) return '—'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = n, i = 0
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`
}
const num = (n: number | null) =>
  n === null || n === undefined ? '—' : n.toLocaleString('en-US')
const when = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 19) + ' UTC' : 'never')

function growth(t: Table) {
  if (t.previous_bytes === null || t.total_bytes === null) return null
  const d = t.total_bytes - t.previous_bytes
  if (d === 0) return null
  return d
}

export default function DatabaseInfo() {
  const [state, setState] = useState<State>({ status: 'loading' })
  const [busy, setBusy] = useState(false)
  const [filter, setFilter] = useState('')

  const load = useCallback(() => {
    api<Payload>('/api/v1/database-info')
      .then((data) => setState({ status: 'ready', data }))
      .catch((e: unknown) => setState({
        status: 'error',
        message: e instanceof ApiError ? e.message : String(e),
      }))
  }, [])

  useEffect(load, [load])

  // While a refresh runs, poll — the work happens out of band and the page
  // should not need a manual reload to notice it finished.
  useEffect(() => {
    if (state.status !== 'ready' || !state.data.refresh.running) return
    const t = setInterval(load, 4000)
    return () => clearInterval(t)
  }, [state, load])

  async function refresh() {
    setBusy(true)
    try {
      await api('/api/v1/database-info/refresh', { method: 'POST', body: '{}' })
      load()
    } catch (e: unknown) {
      setState({ status: 'error', message: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  if (state.status === 'loading') return <p className="muted">Loading…</p>
  if (state.status === 'error') {
    return <div className="panel error">Could not load — {state.message}</div>
  }

  const { data } = state
  const running = data.refresh.running
  // Alphabetical by qualified name (Ben, 2026-08-30). The snapshot returns
  // them biggest-first, which is the right order for "what is eating the
  // disk" and the wrong one for "is table X here" — and the second question
  // is the one the filter box above exists to answer.
  const shown = data.tables
    .filter((t) => !filter
      || `${t.schema}.${t.table}`.toLowerCase().includes(filter.toLowerCase()))
    .slice()
    .sort((a, b) => `${a.schema}.${a.table}`.localeCompare(`${b.schema}.${b.table}`))

  return (
    <>
      {/* h2, not h1: Status owns the page heading, and a second h1 inside a
          tab panel breaks the heading order a screen reader walks. */}
      <h2 className="st-h2">Database</h2>
      <p className="page-sub">
        Sizes and row counts from the last snapshot — <strong>{when(data.collected_at)}</strong>.
        Nothing here is counted at page load: an exact count across the tracking
        tables takes minutes, so a nightly job writes this and a refresh queues
        a new one.
      </p>

      <div className="di-bar">
        <div className="di-stat">
          <div className="t-metric">{data.totals.tables}</div><div className="t-label">tables</div>
        </div>
        <div className="di-stat">
          <div className="t-metric">{bytes(data.totals.bytes)}</div><div className="t-label">on disk</div>
        </div>
        <div className="di-stat">
          <div className="t-metric">{num(data.totals.rows)}</div><div className="t-label">rows (est.)</div>
        </div>
        <div className="di-actions">
          <input className="di-filter" placeholder="Filter tables"
                 value={filter} onChange={(e) => setFilter(e.target.value)} />
          <button className="di-refresh" onClick={() => void refresh()}
                  disabled={busy || running}>
            <RefreshCw size={14} className={running ? 'is-spinning' : ''} aria-hidden />
            {running ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
      </div>

      {running && (
        <div className="panel di-note">
          A refresh is running — started {when(data.refresh.started_at)}. It counts
          smaller tables exactly and leaves the largest on their estimate, because
          counting those is what makes it slow. This page will update itself.
        </div>
      )}
      {data.refresh.error && (
        <div className="panel error di-note">Last refresh failed: {data.refresh.error}</div>
      )}

      <table className="di-table">
        <thead>
          <tr>
            <th>Table</th>
            <th className="di-num">Rows</th>
            <th className="di-num">Dead</th>
            <th className="di-num">Total</th>
            <th className="di-num">Table</th>
            <th className="di-num">Indexes</th>
            <th className="di-num">Change</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((t) => {
            const d = growth(t)
            return (
              <tr key={`${t.schema}.${t.table}`}>
                <td className="mono">
                  {t.schema !== 'public' && <span className="di-schema">{t.schema}.</span>}
                  {t.table}
                </td>
                <td className="di-num">
                  {num(t.rows)}
                  {!t.rows_exact && <span className="di-est" title="Catalog estimate, not an exact count">≈</span>}
                </td>
                <td className="di-num muted">{t.dead_rows ? num(t.dead_rows) : '—'}</td>
                <td className="di-num">
                  {bytes(t.total_bytes)}
                  {t.bloat_ratio !== null && (
                    <span className="di-bloat"
                          title={`${t.bloat_ratio}× this table's leanest recorded bytes per row — dead space a plain vacuum cannot return. Consider VACUUM FULL after the next mass delete.`}>
                      bloat {t.bloat_ratio}×
                    </span>
                  )}
                </td>
                <td className="di-num muted">{bytes(t.table_bytes)}</td>
                <td className="di-num muted">{bytes(t.index_bytes)}</td>
                <td className={`di-num ${d && d > 0 ? 'di-up' : d ? 'di-down' : 'muted'}`}>
                  {d ? `${d > 0 ? '+' : '−'}${bytes(Math.abs(d))}` : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="muted di-note">
        <span className="di-est">≈</span> is the catalog estimate maintained by
        ANALYZE — free, and usually within a percent or two. Exact counts appear
        after a refresh, and only for tables small enough that counting them is
        not itself the problem. Change compares against the snapshot of{' '}
        {when(data.previous_at)}. Dead is rows deleted but not yet reclaimed —
        small numbers are autovacuum working; a bloat mark means the table
        occupies several times its own leanest bytes-per-row and only a
        VACUUM FULL returns that space.
      </p>
    </>
  )
}
